/**
 * @module SyncFromDeviceModal
 * @description Modal that reads a lane's on-box identity live from the device via
 * SSM (GET /fleet/lanes/{laneId}/detect) and AUTOMATICALLY applies the fields that
 * actually drift — deviceId and ipAddress — through the existing lane-update PUT
 * (onApply), then shows a read-only summary of what was changed.
 *
 * Only deviceId and ipAddress are written. Every other detected field (stage,
 * enterprise config/URL, web UI URL, ISS host, lane type, store id) is shown as
 * read-only INFO and never written, because:
 *   - laneType / storeId are grouping keys the UI matches exactly; a bad value
 *     would make the lane render nowhere or jump store groups.
 *   - deploymentStage steers the cross-account SSM role (resolveSsmForLane), so a
 *     wrong rewrite would break restart/modules/deploy-status/detect for the lane.
 *   - enterpriseConfig/enterpriseUrl/webUiUrl are derived/synthesized server-side
 *     (not read from the device), so they must not overwrite a curated record.
 * deviceId / ipAddress are written when the device value differs from the record —
 * including when the record value is blank, so Sync populates a missing Device ID /
 * IP from the device (these two identity fields are exactly what the feature exists
 * to fill/correct). A blank device value is never written over a populated record.
 * When the device already matches on those two fields, an explicit "in sync" state
 * is shown and nothing is written. Opening the modal is the apply action.
 *
 * The Device ID and IP are ALWAYS rendered as read-only rows once a detect
 * succeeds (before-sync value vs on-device value + a per-field status), so the two
 * written fields are visible even when in sync — not just when they drift.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, RefreshCw, Check, AlertTriangle } from 'lucide-react';
import './SyncFromDeviceModal.css';

/**
 * Fields that are AUTO-APPLIED to the fleet record. Kept deliberately narrow to
 * the values that actually drift (a DHCP IP, a swapped device id) and that the
 * device is authoritative for. Must stay a subset of the backend updateLane
 * allow-list.
 */
const APPLY_LABELS = {
  deviceId: 'Device ID',
  ipAddress: 'IP Address',
};
const APPLY_ORDER = Object.keys(APPLY_LABELS);

/**
 * Fields shown as read-only INFO (detected but never written). These are either
 * grouping/credential keys that are dangerous to rewrite, or values the server
 * derives rather than reads off the device — see the module doc for the rationale.
 */
const INFO_LABELS = {
  laneType: 'Lane Type',
  storeId: 'Store ID',
  deploymentStage: 'Stage',
  enterpriseConfig: 'Enterprise',
  enterpriseUrl: 'Enterprise URL',
  webUiUrl: 'Web UI URL',
  issHost: 'ISS Host',
};
const INFO_ORDER = Object.keys(INFO_LABELS);

/** Normalizes a value for display + comparison (null/undefined → ''). */
function norm(v) { return (v === undefined || v === null) ? '' : String(v); }

/**
 * Derives the always-visible Device ID + IP rows from pure inputs — no fetch,
 * timers, or DOM — so the display logic is unit-testable in isolation.
 *
 * "Before sync" (`rec`) is the TRUE pre-sync record value. For a field auto-applied
 * this sync it comes from appliedChanges[].current (which is norm(lane[f]) captured
 * at apply time in runAutoSync Step 2); otherwise it falls through to the live
 * norm(lane[f]). Deriving it from what was actually WRITTEN — rather than a
 * moment-in-time snapshot — keeps "Before sync" correct across the modal's whole
 * lifetime, INCLUDING a Re-scan after a successful apply: Re-scan clears
 * appliedChanges, so `rec` falls through to the live record, which by then equals
 * the device, and the row correctly reads "In sync" instead of a false
 * "Differs — not applied". A read-succeeded-but-apply-FAILED field is not in
 * appliedChanges, so it still compares the unwritten record against the device and
 * correctly reads "Differs — not applied".
 *
 * @param {object} lane the current lane record (may be refreshed by the parent while open).
 * @param {object|null} detected the device-reported values, or null before a detect succeeds.
 * @param {Array|null} appliedChanges fields written this sync ({field,current,detected}), or null.
 * @return {Array} one row per written field: {field,label,rec,dev,changed,status}; empty when no detect.
 */
export function deriveIdentityRows(lane, detected, appliedChanges) {
  if (!detected) return [];
  const appliedByField = new Map((appliedChanges || []).map(c => [c.field, c]));
  return APPLY_ORDER.map(f => {
    const applied = appliedByField.get(f);
    const rec = applied ? applied.current : norm(lane[f]);
    const dev = norm(detected[f]);
    const differs = dev !== '' && dev !== rec;
    return {
      field: f,
      label: APPLY_LABELS[f],
      rec,
      dev,
      changed: Boolean(applied),
      status: applied
        ? 'Updated from device'
        : differs
          ? 'Differs — not applied'
          : dev !== '' ? 'In sync' : '—',
    };
  });
}

function SyncFromDeviceModal({ lane, fleetApi, onApply, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [applyError, setApplyError] = useState(null);
  const [detected, setDetected] = useState(null);
  /** The diff rows that were auto-applied ({field,label,current,detected}); null until a sync completes. */
  const [appliedChanges, setAppliedChanges] = useState(null);

  /**
   * Guards against setState after the modal unmounts mid-SSM-fetch (fetch can take 5–10s).
   * Set true on (re)mount and false on unmount — under React 18 StrictMode the effect runs
   * setup → cleanup → setup, so the ref MUST be re-armed in setup or it stays false and every
   * guarded setState is skipped (the sync result would never render).
   */
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  /**
   * Ensures the auto-apply runs at most once per modal open. React 18 StrictMode
   * double-invokes the mount effect (dev), and a parent re-render can change the
   * lane prop identity; without this guard the detect+PUT could fire twice. A
   * manual Re-scan passes { manual: true } to deliberately bypass it.
   */
  const startedRef = useRef(false);

  const runAutoSync = useCallback(async ({ manual = false } = {}) => {
    if (!manual && startedRef.current) return;
    startedRef.current = true;

    setLoading(true);
    setError(null);
    setApplyError(null);
    setAppliedChanges(null);

    /** Step 1 — read the device config. */
    let det;
    try {
      const data = await fleetApi(`/fleet/lanes/${lane.laneId}/detect`);
      if (!mountedRef.current) return;
      det = data.detected || {};
      setDetected(det);
    } catch (e) {
      if (!mountedRef.current) return;
      setError(e.message || 'Failed to detect device configuration');
      setDetected(null);
      setLoading(false);
      return;
    }

    /**
     * Step 2 — compute the APPLIED fields that genuinely changed: only deviceId /
     * ipAddress, where the device reports a non-empty value that differs from the
     * record. A blank record field IS filled from the device (these two identity
     * fields are exactly what Sync exists to populate/correct); a blank device
     * value is never written over a populated record, and INFO fields are never
     * written — so the sync only ever writes a real device-reported identity.
     */
    const changes = APPLY_ORDER
      .filter(f => norm(det[f]) !== '' && norm(det[f]) !== norm(lane[f]))
      .map(f => ({ field: f, label: APPLY_LABELS[f], current: norm(lane[f]), detected: norm(det[f]) }));

    if (changes.length === 0) {
      /** Nothing differs — leave appliedChanges null so the in-sync panel renders. */
      if (mountedRef.current) setLoading(false);
      return;
    }

    /** Step 3 — auto-apply the differing identity fields through the existing lane PUT. */
    const payload = {};
    changes.forEach(c => { payload[c.field] = c.detected; });
    try {
      await onApply(lane.laneId, payload);
      if (!mountedRef.current) return;
      setAppliedChanges(changes);
    } catch (e) {
      if (mountedRef.current) setApplyError(e.message || 'Failed to apply detected changes to the lane');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [lane, fleetApi, onApply]);

  useEffect(() => { runAutoSync(); }, [runAutoSync]);

  const rescan = () => runAutoSync({ manual: true });
  const isInSync = !loading && !error && !applyError && detected && !appliedChanges;

  /** Read-only detected INFO rows (never written): shown once a detect succeeds. */
  const infoRows = detected
    ? INFO_ORDER
        .filter(f => norm(detected[f]) !== '')
        .map(f => ({ field: f, label: INFO_LABELS[f], value: norm(detected[f]) }))
    : [];

  /**
   * The two WRITTEN identity fields (Device ID + IP), rendered as read-only rows
   * whenever a detect succeeds — so their values are ALWAYS visible, including the
   * in-sync case (the old UI only showed a "nothing to update" banner with no
   * values). See deriveIdentityRows for how "Before sync" is pinned to the true
   * pre-sync value (from appliedChanges, not a moment-in-time snapshot) so it stays
   * correct across a Re-scan following a successful apply.
   */
  const identityRows = deriveIdentityRows(lane, detected, appliedChanges);

  /**
   * True when the device actually reported at least one identity field. detect can
   * succeed yet return an empty object (det = data.detected || {} in runAutoSync),
   * which is truthy — so the render block still fires with nothing applied. The
   * "In sync" banner must be gated on this, or an empty detect would falsely claim
   * "Device ID and IP match the fleet record" while every On-device cell is blank.
   */
  const deviceReportedIdentity = identityRows.some(r => r.dev !== '');

  /**
   * The identity fields the device ACTUALLY reported, used to word the in-sync
   * banner so it names only those — a device that reports just one field must not
   * claim both "match" while the other row renders "—". 0 reported → the banner
   * is not shown at all (see deviceReportedIdentity gate); 1 → "Device ID matches";
   * 2 → "Device ID and IP Address match".
   */
  const inSyncLabels = identityRows.filter(r => r.dev !== '').map(r => r.label);
  const inSyncSummary = `${inSyncLabels.join(' and ')} ${inSyncLabels.length === 1 ? 'matches' : 'match'} the fleet record.`;

  return (
    <div className="fleet-sync-overlay" onClick={onClose}>
      <div className="fleet-sync-modal" onClick={e => e.stopPropagation()}>
        <div className="fleet-sync-head">
          <h3>Sync from device — {lane.hardwareId || lane.laneId}</h3>
          <button className="fleet-sync-close" onClick={onClose} aria-label="Close"><X size={16} /></button>
        </div>
        <div className="fleet-sync-sub">
          Reads this lane live from the device via SSM (DSN {lane.dsn || '—'}) and updates the Device ID and IP Address on the fleet record if they have drifted or are missing. Other detected values are shown for reference only.
        </div>

        {loading && (
          <div className="fleet-modules-loading">
            <span className="fleet-modules-spinner" />
            <div>
              <div>Reading device via SSM and applying changes…</div>
              <div className="fleet-modules-loading-hint">This may take 5–10 seconds</div>
            </div>
          </div>
        )}

        {error && (
          <div className="fleet-modules-error">
            <AlertTriangle size={14} /> {error}
            <div className="fleet-modules-error-hint">The device may be offline or unreachable. Try Re-scan.</div>
          </div>
        )}

        {applyError && !loading && (
          <div className="fleet-modules-error">
            <AlertTriangle size={14} /> {applyError}
            <div className="fleet-modules-error-hint">The device was read successfully but the lane record could not be updated. Try Re-scan.</div>
          </div>
        )}

        {!loading && !error && detected && (
          <>
            {appliedChanges && appliedChanges.length > 0 ? (
              <div className="fleet-sync-applied">
                <Check size={14} /> Synced {appliedChanges.length} field{appliedChanges.length === 1 ? '' : 's'} from the device.
              </div>
            ) : (!applyError && (
              deviceReportedIdentity ? (
                <div className="fleet-sync-insync">
                  <Check size={18} /> In sync — {inSyncSummary}
                </div>
              ) : (
                /**
                 * detect succeeded but reported no Device ID / IP — never claim
                 * "in sync" here (there is nothing to compare against). Re-scan
                 * stays available via the actions row below (isInSync is true).
                 */
                <div className="fleet-modules-error">
                  <AlertTriangle size={14} /> The device did not report a Device ID or IP Address to compare.
                  <div className="fleet-modules-error-hint">The device may be partially provisioned or returned no identity. Try Re-scan.</div>
                </div>
              )
            ))}

            {/**
             * Device ID + IP are ALWAYS shown once a detect succeeds — the two
             * written fields, visible whether they were just updated or already
             * in sync. "Before sync" is the record value at open; "On device" is
             * what the device reported. A field the device did not report shows —.
             */}
            <table className="fleet-modules-table fleet-sync-table">
              <thead>
                <tr>
                  <th>Field</th>
                  <th>Before sync</th>
                  <th>On device</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {identityRows.map(r => (
                  <tr key={r.field}>
                    <td>{r.label}</td>
                    <td className={r.changed ? 'fleet-sync-old' : ''}>{r.rec || <span className="fleet-mod-na">—</span>}</td>
                    <td className="fleet-sync-new">{r.dev || <span className="fleet-mod-na">—</span>}</td>
                    <td>{r.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {!loading && (error || applyError || isInSync || (appliedChanges && appliedChanges.length > 0)) && (
          <div className="fleet-sync-actions">
            <button className="btn btn-secondary btn-sm" onClick={rescan}>
              <RefreshCw size={14} /> Re-scan
            </button>
          </div>
        )}

        {!loading && !error && infoRows.length > 0 && (
          <div className="fleet-sync-info">
            <div className="fleet-sync-info-title">Detected on device (reference only — not written)</div>
            <table className="fleet-modules-table fleet-sync-table">
              <tbody>
                {infoRows.map(r => (
                  <tr key={r.field}>
                    <td>{r.label}</td>
                    <td className="fleet-sync-info-val"><code>{r.value}</code></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default SyncFromDeviceModal;
